import { Button } from "@narmi/design_system";
import { Link, RouteObject, useNavigate, useParams } from "react-router-dom";
import { useUserConfiguration } from "../../hooks/useUserConfiguration";
import { TopBarPageContainer } from "../../components/PageContainer";
import { PermissionedRoutes } from "@components/PermissionedRoutes";

/**
 * Routed outside `NAF_ROUTES`, which sits behind the app-management gate in
 * pages/routes.tsx: a run-only role must still reach its launch link. Access is enforced
 * by the backend.
 */
export const NAF_RUN_ROUTE: RouteObject = {
  path: "naf/:nafId/run",
  async lazy() {
    const { NafIFrame } = await import("./NafIFrame");
    return {
      Component() {
        const navigate = useNavigate();
        const { permissions } = useUserConfiguration();
        const canManageApps =
          permissions.includes("public_api.list_oauth_applications") ||
          permissions.includes("public_api.list_own_oauth_applications");

        return (
          <TopBarPageContainer
            title="Run NAF App"
            // A run-only user cannot open /naf.
            backIconOnClick={canManageApps ? () => navigate("/naf") : undefined}
          >
            <NafIFrame />
          </TopBarPageContainer>
        );
      },
    };
  },
};

export const NAF_ROUTES: RouteObject[] = [
  {
    index: true,
    async lazy() {
      const { ListPageContent } = await import("./NafList");
      return {
        Component() {
          const { permissions } = useUserConfiguration();

          const getActionButton = () => {
            if (permissions.includes("public_api.create_oauth_applications")) {
              return (
                <Link to="/naf/create">
                  <Button size="xs" label="Register new app" />
                </Link>
              );
            } else {
              return null;
            }
          };

          return (
            <TopBarPageContainer title="NAF Apps" action={getActionButton()}>
              <ListPageContent />
            </TopBarPageContainer>
          );
        },
      };
    },
  },
  {
    path: "create",
    element: <PermissionedRoutes requiredPermissions={["public_api.create_oauth_applications"]} />,
    children: [
      {
        index: true,
        async lazy() {
          const { NafAppForm } = await import("./NafAppForm");
          return {
            Component() {
              const navigate = useNavigate();

              return (
                <TopBarPageContainer
                  title="Register a NAF App"
                  backIconOnClick={() => navigate("/naf")}
                >
                  <NafAppForm />
                </TopBarPageContainer>
              );
            },
          };
        },
      },
    ],
  },
  {
    path: ":nafId/tokens",
    async lazy() {
      const { TokensListPageContent } = await import("./NafTokensList");
      return {
        Component() {
          const { permissions } = useUserConfiguration();
          const navigate = useNavigate();
          const { nafId } = useParams();

          return (
            <TopBarPageContainer
              title="NAF Authorized Tokens"
              action={
                permissions.includes("public_api.create_access_tokens") ? (
                  <Button
                    size="xs"
                    onClick={() => navigate(`/naf/${nafId}/token/create`)}
                    label="Add new token"
                  />
                ) : null
              }
              backIconOnClick={() => navigate("/naf")}
            >
              <TokensListPageContent />
            </TopBarPageContainer>
          );
        },
      };
    },
  },
  {
    path: ":nafId/token/create",
    element: <PermissionedRoutes requiredPermissions={["public_api.create_access_tokens"]} />,
    children: [
      {
        index: true,
        async lazy() {
          const { TokenCreatePageContent } = await import("./NafTokenCreate");
          return {
            Component() {
              const navigate = useNavigate();
              const { nafId } = useParams();

              return (
                <TopBarPageContainer
                  title="Register a NAF access token"
                  backIconOnClick={() => navigate(`/naf/${nafId}/tokens`)}
                >
                  <TokenCreatePageContent />
                </TopBarPageContainer>
              );
            },
          };
        },
      },
    ],
  },
  {
    path: ":nafId",
    element: <PermissionedRoutes requiredPermissions={["public_api.edit_oauth_applications"]} />,
    children: [
      {
        index: true,
        async lazy() {
          const { EditPageContent } = await import("./NafEdit");
          return {
            Component() {
              const navigate = useNavigate();

              return (
                <TopBarPageContainer
                  title="Edit a NAF App"
                  backIconOnClick={() => navigate("/naf")}
                >
                  <EditPageContent />
                </TopBarPageContainer>
              );
            },
          };
        },
      },
    ],
  },
];
